import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clampSeatCenter,
  loadOffsetsForBlock,
  saveOffsetsForBlock,
  clearOffsetsForBlock,
} from '../../../poker-ui/seatLayoutOffsets';

/** Вход в компактную раскладку стола (мобильный «столбик»). */
const NARROW_ENTER_PX = 820;
/** Выход из компактной раскладки — шире, чтобы не дребезжать у порога (скроллбар, visualViewport). */
const NARROW_LEAVE_PX = 896;

function readBelotViewportFlags(prevAutoCompact, layoutPreset) {
  if (typeof window === 'undefined') {
    return {
      isNarrowWidth: false,
      autoCompactLayout: false,
      isShortViewport: false,
      isLandscapeOrientation: false,
      viewportWidth: 0,
      viewportHeight: 0,
    };
  }
  const w = window.innerWidth;
  const h = window.innerHeight;
  const isNarrowWidth = w <= NARROW_ENTER_PX;
  let autoCompactLayout;
  if (layoutPreset === 'auto') {
    const prev = typeof prevAutoCompact === 'boolean' ? prevAutoCompact : isNarrowWidth;
    if (!prev && w <= NARROW_ENTER_PX) autoCompactLayout = true;
    else if (prev && w >= NARROW_LEAVE_PX) autoCompactLayout = false;
    else autoCompactLayout = prev;
  } else {
    autoCompactLayout = isNarrowWidth;
  }
  return {
    isNarrowWidth,
    autoCompactLayout,
    isShortViewport: h <= 520,
    isLandscapeOrientation: w > h,
    viewportWidth: w,
    viewportHeight: h,
  };
}

export function useBelotSeatLayout({
  sessionStarted,
  seatArenaRef,
  storageScope,
  storageBlock,
}) {
  const [seatArrangeMode, setSeatArrangeMode] = useState(false);
  const [seatOffsetBySeat, setSeatOffsetBySeat] = useState({});
  const [viewportFlags, setViewportFlags] = useState(() =>
    readBelotViewportFlags(undefined, 'auto'),
  );
  const [layoutPreset, setLayoutPreset] = useState('auto');
  const layoutPresetRef = useRef(layoutPreset);
  layoutPresetRef.current = layoutPreset;
  const seatDragRef = useRef(null);

  const { isNarrowWidth, autoCompactLayout, isShortViewport, isLandscapeOrientation, viewportWidth, viewportHeight } =
    viewportFlags;

  const isCompactTableViewport =
    layoutPreset === 'auto' ? Boolean(autoCompactLayout) : layoutPreset === 'mobile-stack';
  const effectiveLayoutPreset =
    layoutPreset === 'auto' ? (isCompactTableViewport ? 'mobile-stack' : 'ellipse') : layoutPreset;
  const scopedStorageBlock = `${storageBlock}:${effectiveLayoutPreset}`;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const update = () => {
      setViewportFlags((prev) =>
        readBelotViewportFlags(prev?.autoCompactLayout, layoutPresetRef.current),
      );
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    const vv = window.visualViewport;
    if (vv && typeof vv.addEventListener === 'function') {
      vv.addEventListener('resize', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      if (vv && typeof vv.removeEventListener === 'function') {
        vv.removeEventListener('resize', update);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setViewportFlags((prev) =>
      readBelotViewportFlags(prev?.autoCompactLayout, layoutPreset),
    );
  }, [layoutPreset]);

  useEffect(() => {
    if (!sessionStarted) {
      setSeatArrangeMode(false);
      return;
    }
    setSeatOffsetBySeat(loadOffsetsForBlock(storageScope, scopedStorageBlock));
  }, [sessionStarted, storageScope, scopedStorageBlock]);

  const handleSeatDragPointerDown = useCallback(
    (e, seat, slotLeft, slotTop) => {
      if (!seatArrangeMode) return;
      const arena = seatArenaRef.current;
      if (!arena) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = arena.getBoundingClientRect();
      const w = Math.max(1, rect.width);
      const h = Math.max(1, rect.height);
      const o = seatOffsetBySeat[seat] || { dLeft: 0, dTop: 0 };
      seatDragRef.current = {
        pointerId: e.pointerId,
        seat,
        slotLeft,
        slotTop,
        odl: o.dLeft,
        odt: o.dTop,
        sx: e.clientX,
        sy: e.clientY,
        aw: w,
        ah: h,
      };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [seatArrangeMode, seatOffsetBySeat, seatArenaRef],
  );

  const handleSeatDragPointerMove = useCallback((e) => {
    const d = seatDragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dxPct = ((e.clientX - d.sx) / d.aw) * 100;
    const dyPct = ((e.clientY - d.sy) / d.ah) * 100;
    let nl = d.odl + dxPct;
    let nt = d.odt + dyPct;
    const c = clampSeatCenter(d.slotLeft + nl, d.slotTop + nt);
    nl = c.leftPct - d.slotLeft;
    nt = c.topPct - d.slotTop;
    setSeatOffsetBySeat((prev) => {
      const cur = prev[d.seat];
      if (cur && cur.dLeft === nl && cur.dTop === nt) return prev;
      return { ...prev, [d.seat]: { dLeft: nl, dTop: nt } };
    });
  }, []);

  const persistOffsets = useCallback(() => {
    setSeatOffsetBySeat((prev) => {
      saveOffsetsForBlock(storageScope, scopedStorageBlock, prev);
      return prev;
    });
  }, [storageScope, scopedStorageBlock]);

  const handleSeatDragPointerUp = useCallback(
    (e) => {
      const d = seatDragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      seatDragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      persistOffsets();
    },
    [persistOffsets],
  );

  const handleSeatDragLostCapture = useCallback(() => {
    const had = seatDragRef.current != null;
    seatDragRef.current = null;
    if (had) persistOffsets();
  }, [persistOffsets]);

  const handleClearSeatOffsets = useCallback(() => {
    clearOffsetsForBlock(storageScope, scopedStorageBlock);
    setSeatOffsetBySeat({});
  }, [storageScope, scopedStorageBlock]);

  return {
    seatArrangeMode,
    setSeatArrangeMode,
    seatOffsetBySeat,
    isNarrowScreen: isCompactTableViewport,
    isCompactTableViewport,
    isNarrowWidth,
    isShortViewport,
    isLandscapeOrientation,
    viewportWidth,
    viewportHeight,
    layoutPreset,
    setLayoutPreset,
    effectiveLayoutPreset,
    setSeatOffsetBySeat,
    handleSeatDragPointerDown,
    handleSeatDragPointerMove,
    handleSeatDragPointerUp,
    handleSeatDragLostCapture,
    handleClearSeatOffsets,
  };
}
